/**
 * Utilities for reading and deriving names from any library item (characters, maps, tokens…).
 *
 * The canonical name lives in the `_name` field as a library slug with a file extension
 * (e.g. "big-wolf.chr", "dungeon-level-1.map").  A legacy `name` field may also be
 * present on older documents; it is used only as a fallback.
 *
 * Call sites should never dereference `_name` or manipulate extensions themselves — use
 * these helpers so the logic stays in one place.
 */

type LibraryItem = Record<string, unknown>;

/**
 * Returns the user-visible in-game display name (no file extension).
 *
 * "wolf.chr"            →  "wolf"
 * "dungeon-level-1.map" →  "dungeon-level-1"
 *
 * Falls back to the deprecated `name` field when `_name` is absent (e.g. an item
 * that pre-dates the library rework), then to an empty string.
 */
export function getInGameFilename(item: LibraryItem): string {
    const libName = item._name as string | undefined;
    if (libName) {
        const dot = libName.lastIndexOf('.');
        return dot > 0 ? libName.slice(0, dot) : libName;
    }
    return (item.name as string) || '';
}

/**
 * Returns the full library name exactly as stored in `_name` (extension included).
 *
 * "wolf.chr"  →  "wolf.chr"
 *
 * Falls back to the deprecated `name` field, then to an empty string.
 */
export function getLibraryName(item: LibraryItem): string {
    return (item._name as string) || (item.name as string) || '';
}

/**
 * Builds a new `_name` value from a user-supplied display stem, preserving the
 * extension already carried by the item.
 *
 * item._name = "wolf.chr", newDisplayName = "Big Wolf"
 * → "Big Wolf.chr"
 *
 * Use this whenever a rename needs to be reflected in local state — it ensures
 * the extension is never accidentally dropped.
 */
export function updateFileName(item: LibraryItem, newDisplayName: string): string {
    const libName = item._name as string | undefined;
    const dot = libName ? libName.lastIndexOf('.') : -1;
    const ext = dot > 0 ? libName!.slice(dot) : '';
    return newDisplayName + ext;
}

/**
 * Builds a library filename for a new item from a user-supplied display name.
 * Strips characters that are illegal in library paths, then appends the given
 * extension (e.g. ".map", ".chr").
 *
 * Spaces and mixed case are preserved — the display name must be kept as-is
 * so users see exactly what they typed.
 *
 * toLibrarySlug('Big Wolf', '.chr') → 'Big Wolf.chr'
 * toLibrarySlug('', '.map')         → 'untitled.map'
 */
export function toLibrarySlug(name: string, ext: string): string {
    const base = (name || 'untitled').trim().replace(/[^a-zA-Z0-9 _.'-]/g, '') || 'untitled';
    return `${base}${ext}`;
}
